import { useEffect, useState, type RefObject } from 'react'

/**
 * Tracks the observed width (in CSS pixels) of a referenced element via
 * `ResizeObserver`. Returns `0` until the first measurement.
 *
 * Lives in `shared/lib/` because it is a generic, framework-level utility
 * with no domain knowledge.
 */
export function useElementWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      // `contentRect.width` is sufficient — we read inner width to compare
      // against the 450 px collapse threshold; padding/border on the layout
      // root is zero so contentRect == bounding box here.
      setWidth(entry.contentRect.width)
    })

    observer.observe(element)
    setWidth(element.getBoundingClientRect().width)

    return () => observer.disconnect()
  }, [ref])

  return width
}
